import { FlatRun } from '@/Pages/Run/utils/utils';
import { MessageUnionType } from '@/types/schema';
import { isOpenAIMessage } from '@/utils/messages';

function extractMessages(
  inputOrOutput?: Record<string, any>,
  runType?: string
) {
  if (runType !== 'llm') {
    return [];
  }
  if (isOpenAIMessage(inputOrOutput)) {
    return [inputOrOutput];
  } else if (Array.isArray(inputOrOutput?.messages)) {
    return inputOrOutput.messages;
  } else {
    return [];
  }
}

export function extractMessagesFromRuns(flatRuns: FlatRun[]) {
  const messageIds = new Set();
  const aggregatedMessages: MessageUnionType[] = [];

  // Build tree structure
  const runsByParentId = flatRuns.reduce((acc, flatRun) => {
    const parentId = flatRun.run.parent_run_id;
    if (parentId == null) {
      return acc;
    }
    if (!acc[parentId]) {
      acc[parentId] = [];
    }
    acc[parentId].push(flatRun);
    return acc;
  }, {});

  // Recursive function to process a run and its children
  function processRun(flatRun: FlatRun): MessageUnionType[] {
    const messages: MessageUnionType[] = [];
    const { run } = flatRun;

    // Add input messages
    const inputMessages = extractMessages(run.inputs, run.run_type);
    for (const message of inputMessages) {
      if (message.id === undefined || !messageIds.has(message.id)) {
        if (message.id !== undefined) {
          messageIds.add(message.id);
        }
        messages.push(message);
      }
    }

    // Process all child runs recursively
    const children = runsByParentId[run.id] ?? [];
    for (const child of children) {
      messages.push(...processRun(child));
    }

    const outputMessages = extractMessages(run.outputs, run.run_type);
    for (const message of outputMessages) {
      if (message.id === undefined || !messageIds.has(message.id)) {
        if (message.id !== undefined) {
          messageIds.add(message.id);
        }
        messages.push(message);
      }
    }

    return messages;
  }

  for (const flatRun of flatRuns) {
    if (flatRun.depth === 0) {
      aggregatedMessages.push(...processRun(flatRun));
    }
  }

  return aggregatedMessages;
}
